import React, {
  useRef,
  useMemo,
  useState,
  useEffect,
} from 'react'
import { connect as connectRedux } from 'react-redux'
import compose from 'recompose/compose'
import PropTypes from 'prop-types'

const DELTA_THRESHOLD = 50
export const SLIDER_INACTIVE_INDEX = -1

function Slider (props) {
  const {
    app,
    slides,
    startIndex,
    onSlideChange,
    activeSlide,
    enableSlideClick,
  } = props

  // Component state
  const [currentIndex, setCurrentIndex] = useState(startIndex)
  const [isTouching, setIsTouching] = useState(false)
  const [touchStartX, setTouchStartX] = useState(0)
  const [touchStartY, setTouchStartY] = useState(0)
  const [touchDeltaX, setTouchDeltaX] = useState(0)
  const [sliderWidth, setSliderWidth] = useState(0)
  const [initialSlideIndex, setInitialSlideIndex] = useState(0)
  const [isSliderEnabled, setIsSliderEnabled] = useState(true)

  const sliderRef = useRef(null)
  const totalSlides = typeof slides === 'object' ? slides.size : 0

  // Function to handle the start of the touch or mouse movement
  const handleStart = (e) => {
    setIsTouching(true)
    const startX = e.touches ? e.touches[0].clientX : e.clientX
    const startY = e.touches ? e.touches[0].clientY : e.clientY
    setTouchStartX(startX)
    setTouchStartY(startY)
  }

  // Function to handle the touch or mouse movement
  const handleMove = (e) => {
    if (!isTouching) return

    const moveX = e.touches ? e.touches[0].clientX : e.clientX
    const moveY = e.touches ? e.touches[0].clientY : e.clientY

    const deltaX = moveX - touchStartX
    const deltaY = moveY - touchStartY

    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      setTouchDeltaX(deltaX)
    } else {
      setTouchDeltaX(0)
    }
  }

  // Function to handle the end of the touch or mouse movement
  const handleEnd = () => {
    setIsTouching(false)
    updateSlide()
    setTouchDeltaX(0)
  }

  // Function to update the slide index based on touch delta
  const updateSlide = () => {
    if (Math.abs(touchDeltaX) > DELTA_THRESHOLD && isSliderEnabled) {
      if (touchDeltaX > 0) {
        handlePrev()
      } else {
        handleNext()
      }
    }
  }

  // Function to move slide on click
  const handleClick = (index) => {
    if (index > currentIndex) {
      handleNext()
    }
    if (index < currentIndex) {
      handlePrev()
    }
  }

  // Functions to navigate between slides
  const handleNext = () => setCurrentIndex((prevIndex) => (prevIndex + 1) % totalSlides)
  const handlePrev = () => setCurrentIndex((prevIndex) => (
    prevIndex - 1 + totalSlides) % totalSlides)

  // Style for slide transformation
  const sliderTransformStyle = useMemo(() => {
    if (!isSliderEnabled || totalSlides === 1) {
      return null
    }

    const isEvenSlidesWithOffset = totalSlides % 2 === 0 && currentIndex !== totalSlides
    const offsetX = isEvenSlidesWithOffset ? sliderWidth / 2 : 0
    const slidePosition = sliderWidth * (Math.abs(currentIndex) - initialSlideIndex)
    const finalPosition = (slidePosition + offsetX) * -1

    return { transform: `translateX(${finalPosition}px)` }
  }, [isSliderEnabled, sliderWidth, currentIndex, initialSlideIndex])

  // Effect to update slider width and enable/disable sliding based on parent width
  useEffect(() => {
    const { offsetWidth, parentNode } = sliderRef.current
    const { offsetWidth: parentOffsetWidth } = parentNode
    const sliderStatus = offsetWidth > parentOffsetWidth
    setIsSliderEnabled(sliderStatus)
    if (!sliderStatus) {
      setCurrentIndex(SLIDER_INACTIVE_INDEX)
    } else {
      setCurrentIndex(Math.abs(currentIndex))
    }
  }, [sliderWidth, app, activeSlide])

  // Effect to initialize the starting slide index and slider width
  useEffect(() => {
    const startSlide = Math.floor(totalSlides / 2)
    setInitialSlideIndex(startSlide)

    const { offsetWidth } = sliderRef.current
    setSliderWidth(offsetWidth / totalSlides)
  }, [app])

  // Effect to handle the slide change callback
  useEffect(() => {
    if (onSlideChange) {
      onSlideChange(currentIndex)
    }
  }, [currentIndex])

  useEffect(() => {
    if (isSliderEnabled) {
      setCurrentIndex(activeSlide)
    }
  }, [activeSlide])

  return (
    <div
      className="slider"
      onMouseDown={handleStart}
      onMouseMove={handleMove}
      onMouseUp={handleEnd}
      onMouseLeave={handleEnd}
      onTouchStart={handleStart}
      onTouchMove={handleMove}
      onTouchEnd={handleEnd}
    >
      <div className="slider__container">
        <div
          className="slider__slides"
          ref={sliderRef}
          style={sliderTransformStyle}
        >
          {slides.map((slide, index) => (
            <div
              key={index}
              className="slider__slide"
              onClick={enableSlideClick ? () => handleClick(index) : () => {}}
            >
              {slide}
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}

// Default prop values
Slider.defaultProps = {
  startIndex: 0,
  enableSlideClick: false,
}

// Prop validation
Slider.propTypes = {
  slides: PropTypes.object.isRequired,
  startIndex: PropTypes.number,
  onSlideChange: PropTypes.func,
  activeSlide: PropTypes.number,
  enableSlideClick: PropTypes.bool,
}

export default compose(
  connectRedux(
    (state) => ({
      app: state.app,
    }),
  ),
)(Slider)
